import {
    Button,
    Checkbox as DSCheckbox,
    Field,
    FieldError,
    FieldLabel,
    Input as DSInput,
    Textarea as DSTextarea,
} from "frontend-components";

import { useFieldContext, useFormContext } from "./Form";
import { CharacterCount } from "@dashboard/components/CharacterCount";
import { VirtualizedUserSelector } from "@dashboard/components/common/VirtualizedUserSelector";

interface InputWithCharCountProps
    extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    label?: string;
    maxLength?: number;
    showCharCount?: boolean;
    charCountWarningThreshold?: number;
}

export function Input(props: InputWithCharCountProps) {
    const field = useFieldContext();
    const {
        label,
        className,
        maxLength,
        showCharCount = false,
        charCountWarningThreshold = 0.8,
        ...inputProps
    } = props;

    const isInvalid = field.state.meta.errors.length > 0;
    const currentLength = String(field.state.value ?? "").length;

    return (
        <Field data-invalid={isInvalid}>
            {label && (
                <FieldLabel htmlFor={field.name} className="text-sm font-medium">
                    {label}
                </FieldLabel>
            )}
            <DSInput
                id={field.name}
                name={field.name}
                className={className}
                maxLength={maxLength}
                {...inputProps}
                value={String(field.state.value ?? "")}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={isInvalid}
            />
            {showCharCount && maxLength !== undefined && (
                <div className="flex justify-end">
                    <CharacterCount
                        currentLength={currentLength}
                        maxLength={maxLength}
                        warningThreshold={charCountWarningThreshold}
                    />
                </div>
            )}
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

interface TextAreaWithCharCountProps
    extends React.DetailedHTMLProps<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>,
        HTMLTextAreaElement
    > {
    label?: string;
    maxLength?: number;
    showCharCount?: boolean;
    charCountWarningThreshold?: number;
}

export function TextArea(props: TextAreaWithCharCountProps) {
    const field = useFieldContext();
    const {
        label,
        className,
        maxLength,
        showCharCount = false,
        charCountWarningThreshold = 0.8,
        ...textareaProps
    } = props;

    const isInvalid = field.state.meta.errors.length > 0;
    const currentLength = String(field.state.value ?? "").length;

    return (
        <Field data-invalid={isInvalid}>
            {label && (
                <FieldLabel htmlFor={field.name} className="text-sm font-medium">
                    {label}
                </FieldLabel>
            )}
            <DSTextarea
                id={field.name}
                name={field.name}
                className={className}
                maxLength={maxLength}
                {...textareaProps}
                value={String(field.state.value ?? "")}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={isInvalid}
            />
            {showCharCount && maxLength !== undefined && (
                <div className="flex justify-end">
                    <CharacterCount
                        currentLength={currentLength}
                        maxLength={maxLength}
                        warningThreshold={charCountWarningThreshold}
                    />
                </div>
            )}
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

export function Checkbox({ label }: { label: string }) {
    const field = useFieldContext();

    return (
        <label className="flex cursor-pointer items-center gap-2">
            <DSCheckbox
                checked={Boolean(field.state.value)}
                onCheckedChange={(checked) => field.handleChange(checked)}
                onBlur={field.handleBlur}
            />
            <span className="text-sm">{label}</span>
        </label>
    );
}

export function UserSelect(props: { label?: string; placeholder?: string }) {
    const field = useFieldContext();
    const hasErrors = field.state.meta.errors.length > 0;

    return (
        <Field data-invalid={hasErrors}>
            <VirtualizedUserSelector
                label={props.label}
                placeholder={props.placeholder}
                value={field.state.value as number | null}
                onChange={(userId) => field.handleChange(userId)}
                hasError={hasErrors}
            />
            {hasErrors && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

export function SubmitButton({ label }: { label: string }) {
    const form = useFormContext();
    return (
        <form.Subscribe selector={(state) => [state.isSubmitting]}>
            {([isSubmitting]) => (
                <Button type="submit" variant="submit" size="submit" disabled={isSubmitting}>
                    {label}
                </Button>
            )}
        </form.Subscribe>
    );
}
